registerComponent("sections/fundos", function (component) {
/**
 * sections/fundos — o accordion com cronômetro de uma seção de produto (porte
 * do `sections/fundos` do asa-com; ver o cabeçalho do index.blade.php).
 *
 * Quatro coisas:
 *
 *   1. O ACCORDION. Um item aberto por vez. O clique no nome (ou em qualquer
 *      ponto do cartão fechado — o `::after` do botão cobre o cartão) abre o
 *      item e reinicia o cronômetro; `aria-expanded` acompanha. A foto troca
 *      junto quando os dados trazem foto por item.
 *
 *   2. O CRONÔMETRO. O anel do item aberto enche em `--fundos-dur` (8s) e, ao
 *      completar, o seguinte abre — do último volta ao primeiro. O tempo É a
 *      animação do anel: quem avança é o `animationend` dela, então o que se vê
 *      e o que acontece não saem de sincronia, e pausar é só
 *      `animation-play-state` (a classe `fundos--pausado`).
 *
 *   3. AS PAUSAS — um conjunto de motivos, como o carrossel do hero (ADR 0070):
 *      `fora` (a seção fora da janela), `oculto` (aba em segundo plano),
 *      `ponteiro` (mouse sobre o cartão ABERTO, só com ponteiro fino), `toque`
 *      (sem hover, o toque no cartão ABERTO pausa e o seguinte solta) e `foco`
 *      (foco de TECLADO na seção). O primeiro a chegar congela o anel; o último
 *      a sair o solta. Com `prefers-reduced-motion` não há cronômetro: o
 *      accordion funciona só pelo clique, e o anel não aparece.
 *
 *   4. A ALTURA DA COLUNA. Mede a maior altura que a coluna pode ter com
 *      qualquer item aberto e a publica em `--fundos-lista-h`: a fileira nasce
 *      com essa altura e não muda na troca — nada abaixo dela se move. Remede
 *      no resize e quando a fonte chega — e só depois que a folha vale.
 *
 * Diferença do global: a variante lista (Mercado de Capitais) mostra no item
 * FECHADO o começo do texto (`.fundos__resumo`), que some quando ele abre. A
 * conta da altura desconta o resumo do item que abre.
 *
 * @param {HTMLElement} component - o <section> da seção (produtos__painel)
 */

const lista = component.querySelector('[data-fundos-lista]');
const itens = lista ? Array.from(lista.querySelectorAll('.fundos__item')) : [];
const fotos = Array.from(component.querySelectorAll('.fundos__foto[data-fundo]'));

if (itens.length > 1) {
    let atual = Math.max(0, itens.findIndex((item) => item.classList.contains('fundos__item--aberto')));
    const auto = Asa.movimentoPermitido();

    const gatilhoDe = (item) => item.querySelector('.fundos__gatilho');
    let sobre = -1;
    let conferirPonteiro = () => {};
    let alternarToque = () => {};
    const anelDe = (item) => item.querySelector('.fundos__anel');

    /* ─────────────────────────────────────────────── a altura da coluna ── */
    /* Para cada item: a altura da coluna com ELE aberto = a soma dos fechados
       − o resumo dele + o corpo dele + os vãos. Medido sem mexer no estado: o
       fechado mede o recuo, o fio e a linha do título (e o resumo, na variante
       lista); o corpo e o resumo medem o `scrollHeight` do miolo, que existe
       mesmo com a linha do grid em 0fr. */
    const medir = () => {
        if (!Asa.desktop.matches) {
            component.style.removeProperty('--fundos-lista-h');
            return;
        }

        const vao = parseFloat(getComputedStyle(lista).rowGap) || 0;
        const resumos = itens.map((item) => {
            const resumo = item.querySelector('.fundos__resumo-inner');

            return resumo ? resumo.scrollHeight : 0;
        });
        const fechados = itens.map((item, i) => {
            const estilo = getComputedStyle(item.querySelector('.fundos__cartao'));
            const bordas = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
                .reduce((soma, prop) => soma + (parseFloat(estilo[prop]) || 0), 0);

            return bordas + item.querySelector('.fundos__cabeca').offsetHeight + resumos[i];
        });
        const corpos = itens.map((item) => item.querySelector('.fundos__corpo-inner').scrollHeight);
        const somaFechados = fechados.reduce((a, b) => a + b, 0);
        const vaos = vao * (itens.length - 1);
        const maior = Math.max(...corpos.map((corpo, i) => somaFechados - resumos[i] + corpo + vaos));

        component.style.setProperty('--fundos-lista-h', `${Math.ceil(maior)}px`);
    };

    /* Só com a folha aplicada: sem ela a foto de dentro do cartão e o anel
       ocupam a largura toda, e a coluna nascia com milhares de px quando a
       `main.min.css` chegava depois dos scripts. */
    Asa.quandoEstilizado(() => {
        medir();
        window.addEventListener('resize', medir, { passive: true });
        Asa.desktop.addEventListener('change', medir);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(medir);
    });

    /* ─────────────────────────────────────────────────── o accordion ── */
    const reiniciarAnel = (item) => {
        itens.forEach((outro) => anelDe(outro).classList.remove('fundos__anel--corre'));

        if (!auto) return;

        const anel = anelDe(item);

        void anel.offsetWidth;            /* força o recomeço da animação */
        anel.classList.add('fundos__anel--corre');
    };

    const abrir = (indice) => {
        const alvo = itens[indice];

        itens.forEach((item, i) => {
            const aberto = i === indice;

            item.classList.toggle('fundos__item--aberto', aberto);
            gatilhoDe(item).setAttribute('aria-expanded', String(aberto));
        });

        fotos.forEach((foto) => {
            foto.classList.toggle('fundos__foto--atual', Number(foto.dataset.fundo) === indice);
        });

        atual = indice;
        reiniciarAnel(alvo);
        conferirPonteiro();
        alternarToque(true);
    };

    itens.forEach((item, i) => {
        gatilhoDe(item).addEventListener('click', (evento) => {
            if (i === atual) return;      /* aberto: o clique segue para o cartão (a pausa do toque) */

            /* Fechado: abre, e o clique para aqui — senão ele chegaria ao
               cartão já aberto. */
            evento.stopPropagation();
            abrir(i);
        });

        /* Sem hover, o toque no cartão aberto pausa o cronômetro, como o mouse
           por cima no desktop (ver `alternarToque`). Um link segue o caminho
           dele. */
        item.querySelector('.fundos__cartao').addEventListener('click', (evento) => {
            if (i !== atual) return;
            if (evento.target instanceof Element && evento.target.closest('a')) return;

            alternarToque();
        });
    });

    /* ─────────────────────────────────────────────── o cronômetro ── */
    if (auto) {
        component.classList.add('fundos--auto');

        const motivos = new Set();

        const pausar = (motivo) => {
            motivos.add(motivo);
            component.classList.add('fundos--pausado');
        };

        const soltar = (motivo) => {
            motivos.delete(motivo);
            if (!motivos.size) component.classList.remove('fundos--pausado');
        };

        conferirPonteiro = () => {
            if (sobre !== -1 && sobre === atual) pausar('ponteiro');
            else soltar('ponteiro');
        };

        /* No toque não há `pointerleave` que solte a pausa, então ela é um
           interruptor — o primeiro toque no cartão aberto pausa, o seguinte
           solta, e abrir outro item também solta (o `abrir` chama isto com
           `forcarSoltar`). Com hover quem pausa é o ponteiro, e o clique não
           muda nada. */
        const semHover = !matchMedia('(hover: hover)').matches;

        alternarToque = (forcarSoltar = false) => {
            if (!semHover) return;

            if (forcarSoltar || motivos.has('toque')) soltar('toque');
            else pausar('toque');
        };

        /* O avanço: o fim do arco é o fim do ciclo. */
        lista.addEventListener('animationend', (evento) => {
            if (evento.animationName !== 'fundos-anel') return;
            if (!itens[atual].contains(evento.target)) return;

            abrir((atual + 1) % itens.length);
        });

        /* Fora da janela: a troca não acontece sem ninguém ver. Começa pausado
           até o observador dizer onde a seção está. */
        pausar('fora');

        if (typeof IntersectionObserver === 'function') {
            new IntersectionObserver(([entrada]) => {
                if (entrada.isIntersecting) soltar('fora');
                else pausar('fora');
            }, { threshold: 0.35 }).observe(lista);
        } else {
            soltar('fora');
        }

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) pausar('oculto');
            else soltar('oculto');
        });

        /* O ponteiro só pausa sobre o cartão ABERTO — o do anel que corre —, e
           não sobre qualquer ponto da fileira. `sobre` é o cartão sob o mouse;
           `abrir` refaz a conta, porque o cronômetro pode abrir justo o cartão
           em que o mouse está parado. */
        if (matchMedia('(hover: hover)').matches) {
            itens.forEach((item, i) => {
                const cartao = item.querySelector('.fundos__cartao');

                cartao.addEventListener('pointerenter', (evento) => {
                    if (evento.pointerType === 'touch') return;
                    sobre = i;
                    conferirPonteiro();
                });
                cartao.addEventListener('pointerleave', () => {
                    if (sobre === i) sobre = -1;
                    conferirPonteiro();
                });
            });
        }

        /* Só o foco de TECLADO pausa: o clique também foca o botão, e pausar
           aí deixaria o cronômetro parado depois de cada clique. */
        component.addEventListener('focusin', (evento) => {
            if (evento.target instanceof Element && evento.target.matches(':focus-visible')) pausar('foco');
        });
        component.addEventListener('focusout', (evento) => {
            if (!(evento.relatedTarget instanceof Node) || !component.contains(evento.relatedTarget)) soltar('foco');
        });

        reiniciarAnel(itens[atual]);
    }
}

});
